import { useDeviceType } from '@cian/ui-kit/responsive';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { MultipinPreviewContainer } from './MultipinPreview';
import { OfferPreviewCardContainer } from './OfferPreviewCardContainer';
import { OFFER_PREVIEW_SAFE_AREA } from './constants';
import * as styles from './styles.css';
import { isCompactDesktopPreview } from './utils/isCompactDesktopPreview';
import { closeOfferPreview } from '../../../common/actions/closeOfferPreview/closeOfferPreview';
import { loadPreviewOffers } from '../../../common/actions/previewOffers/loadPreviewOffers';
import { OfferPreviewBalloon } from '../../../common/components/OfferCardPreview/components/OfferPreviewBalloon';
import { OfferPreviewError } from '../../../common/components/OfferCardPreview/components/OfferPreviewError';
import { OfferPreviewLoading } from '../../../common/components/OfferCardPreview/components/OfferPreviewLoading';
import { OfferPreviewModal } from '../../../common/components/OfferCardPreview/components/OfferPreviewModal';
import { getDesktopPreviewSize } from '../../../common/components/OfferCardPreview/utils/getDesktopPreviewSize';
import { getPinAnchor } from '../../../common/constants/clusterPins/getPinAnchor';
import {
  selectActivePreviewOffer,
  selectPreviewOffersStatus,
  selectPreviewRealtyIds,
} from '../../../common/selectors/previewOffers';
import { EFetchStatus } from '../../../common/types/listingOffers';
import { TThunkDispatch } from '../../../common/types/redux';
import {
  selectActiveFeature,
  selectActiveOfferId,
  selectIsMultipinSelected,
  selectIsOfferPreviewOpen,
} from '../../selectors';
import { useMaxPinsPerCluster } from '../Map/hooks/useMaxPinsPerCluster';

export interface IOfferPreviewContainerProps {
  listingSafeAreaWidth: number;
  map: YMaps.Map;
  ymaps: YMaps.IYMaps;
}

export const OfferPreviewContainer = ({ listingSafeAreaWidth, ymaps, map }: IOfferPreviewContainerProps) => {
  const dispatch = useDispatch<TThunkDispatch>();
  const deviceType = useDeviceType();
  const maxPinsPerCluster = useMaxPinsPerCluster();
  const activeFeature = useSelector(selectActiveFeature);
  const activeOfferRealtyId = useSelector(selectActiveOfferId);
  const isPreviewOpen = useSelector(selectIsOfferPreviewOpen);
  const isMultipinSelected = useSelector(selectIsMultipinSelected);
  const offer = useSelector(selectActivePreviewOffer);
  const isPreviewOfferLoading = useSelector(selectPreviewOffersStatus) === EFetchStatus.LOADING;
  const pinRealtyIds = useSelector(selectPreviewRealtyIds);
  const isPhone = deviceType === 'phone';

  const onClose = React.useCallback(() => {
    dispatch(closeOfferPreview());
  }, [dispatch]);

  const retryPreviewOffer = React.useCallback(() => {
    dispatch(loadPreviewOffers(pinRealtyIds));
  }, [dispatch, pinRealtyIds]);

  const safeArea = React.useMemo(
    () => ({ ...OFFER_PREVIEW_SAFE_AREA, left: listingSafeAreaWidth }),
    [listingSafeAreaWidth],
  );

  // Режим превью фиксируется при выборе объявления по realtyId.
  // Resize и обновление геометрии пина не меняют режим открытого превью.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const isCompactPreview = React.useMemo(() => isCompactDesktopPreview(), [activeOfferRealtyId]);
  const isCompact = !isPhone && isCompactPreview;
  const { width: previewWidth, height: previewHeight } = getDesktopPreviewSize(isCompactPreview);
  const cardClassName = isPhone ? styles['card_phone'] : styles['card_desktop'];

  if (!isPreviewOpen) {
    return null;
  }

  // Мультипин: телефон показывает шторку со всеми объявлениями, десктоп - балун с одним выбранным.
  if (isPhone && isMultipinSelected && activeFeature) {
    return <MultipinPreviewContainer feature={activeFeature} onClose={onClose} />;
  }

  const content = (() => {
    if (!offer) {
      if (isPreviewOfferLoading) {
        return <OfferPreviewLoading className={cardClassName} isCompact={isCompact} />;
      }

      return (
        <OfferPreviewError
          className={cardClassName}
          isCompact={isCompact}
          onClose={onClose}
          onRetry={retryPreviewOffer}
        />
      );
    }

    return (
      <OfferPreviewCardContainer
        className={cardClassName}
        hasCloseControl={isPhone}
        isCompact={isCompact}
        showButtons
        narrowButtons={!isPhone}
        offer={offer}
      />
    );
  })();

  if (isPhone) {
    return (
      <OfferPreviewModal isOpen={isPreviewOpen} onClose={onClose}>
        {content}
      </OfferPreviewModal>
    );
  }

  if (!activeFeature) {
    return null;
  }

  const anchor = getPinAnchor(activeFeature.properties, maxPinsPerCluster);

  return (
    <OfferPreviewBalloon
      activeFeature={activeFeature}
      anchor={anchor}
      ymaps={ymaps}
      map={map}
      onClose={onClose}
      previewHeight={previewHeight}
      previewWidth={previewWidth}
      safeArea={safeArea}
    >
      {content}
    </OfferPreviewBalloon>
  );
};

OfferPreviewContainer.displayName = 'OfferPreviewContainer';
